import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { formatCurrencyString } from "../../../utils/string.utils";
import AppText from "../../Core/AppText/AppText";

interface SalaryJournalSummaryProps {
	totalGrossPay: number;
	totalHourlyPay: number;
	totalHolidayPay: number;
	totalSickPay: number;
	totalDeduction: number;
	totalQualifyingDeduction: number;
	totalOb: number;
}
interface SalaryRowSummaryProps {
	label: string;
	total: number | string;
}

const SalaryJournalSummary: React.FC<SalaryJournalSummaryProps> = ({
	totalGrossPay,
	totalHolidayPay,
	totalHourlyPay,
	totalSickPay,
	totalDeduction,
	totalQualifyingDeduction,
	totalOb,
}) => {
	const { t } = useTranslation();

	const SalaryRowSummary: React.FC<SalaryRowSummaryProps> = ({ label, total }) => {
		return (
			<tr>
				<td>{label}</td>
				<td className="right-align">{total}</td>
			</tr>
		);
	};

	return (
		<StyledPerSalary>
			<AppText margin="0 0 20px 0">{t("salary.journal.byTypeOfSalary")}</AppText>
			<table>
				<tbody>
					<SalaryRowSummary
						label={t("salary.journal.salaryTypes.hourly")}
						total={formatPrice(totalHourlyPay)}
					/>
					<SalaryRowSummary
						label={t("salary.journal.salaryTypes.ob")}
						total={formatPrice(totalOb)}
					/>
					<SalaryRowSummary
						label={t("salary.journal.salaryTypes.holidayPay")}
						total={formatPrice(totalHolidayPay)}
					/>

					<SalaryRowSummary
						label={t("salary.journal.salaryTypes.sickPay")}
						total={formatPrice(totalSickPay)}
					/>
					<SalaryRowSummary
						label={t("salary.journal.salaryTypes.sickPayNonPaid")}
						total={formatPrice(0)}
					/>
					<SalaryRowSummary
						label={t("salary.journal.salaryTypes.qualifyingDeduction")}
						total={formatPrice(
							totalQualifyingDeduction > 0
								? totalQualifyingDeduction * -1
								: totalQualifyingDeduction
						)}
					/>
					<SalaryRowSummary
						label={t("salary.journal.salaryTypes.leaveOfAbsence")}
						total={formatPrice(0)}
					/>
					<SalaryRowSummary
						label={t("salary.journal.salaryTypes.childCare")}
						total={formatPrice(0)}
					/>
					<SalaryRowSummary
						label={t("salary.journal.salaryTypes.deduction")}
						total={formatPrice(totalDeduction > 0 ? totalDeduction * -1 : totalDeduction)}
					/>
					<tr className="total-gross-row">
						<td colSpan={2} className="total-gross">
							<AppText size="l" color="black" weight="medium" textAlign="right">
								{t("TOTAL")} {formatPrice(totalGrossPay)}
							</AppText>
						</td>
					</tr>
				</tbody>
			</table>
		</StyledPerSalary>
	);
};

export default SalaryJournalSummary;

const StyledPerSalary = styled.div`
	table {
		border-collapse: collapse;

		th {
			font-weight: ${({ theme }) => theme.fontWeight.FONT_WEIGHT_MEDIUM};
		}

		tbody tr td {
			padding: 0 0 5px 0;
		}

		tbody tr:nth-last-child(2) td {
			padding-bottom: 10px;
		}
	}
	& > p {
		font-size: 1.5rem;
		color: ${({ theme }) => theme.colors.COLOR_BLACK};
		font-weight: 700;
	}
`;

function formatPrice(num: number) {
	return formatCurrencyString(num, { maximumFractionDigits: 2 });
}
